import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { lastValueFrom } from 'rxjs';

import { Loading } from 'src/shared/components/loading/loading';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { ConciergeApiService } from 'src/shared/services/concierge-api.service';
import { Client } from 'src/providers/client/client';
import { GuestReservations } from 'src/providers/guest-reservations/guest-reservations';
import { Util } from 'src/shared/util/util';
import { WindowService } from 'src/shared/providers/window.service';

import { TidySelectNumberValueModel, TidySelectStringValueModel } from 'src/models/tidy-select-item.model';
import { ParseDateTimePipe } from 'src/shared/pipes/parse-date.pipe';
import { TranslationPipe } from 'src/shared/pipes/translation.pipe';

@Component({
    templateUrl: 'add-upsell.html',
    standalone: false
})
export class AddUpsellPage implements OnInit, OnDestroy {
  form: UntypedFormGroup;
  errorMessage: string;
  dialogParams: any;
  isRightSideContent = true;
  loaded: boolean;
  submitted: boolean;
  addressResponse: any;
  addressFilter: TidySelectNumberValueModel[] = [];
  requestTypeItems: TidySelectStringValueModel[] = [];
  reservationItems: TidySelectNumberValueModel[] = [];

  isEditMode = false;
  editingUpsellId: string | null = null;
  editingUpsellData: any = null;

  boundAddUpsell = this.addUpsell.bind(this);

  private readonly fb = inject(UntypedFormBuilder);
  private readonly storage = inject(TidyStorage);
  private readonly navCtrl = inject(CustomNavController);
  private readonly rightSidePanelService = inject(RightSidePanelService);
  private readonly conciergeApi = inject(ConciergeApiService);
  private readonly client = inject(Client);
  private readonly guestReservations = inject(GuestReservations);
  private readonly util = inject(Util);
  private readonly windowService = inject(WindowService);

  constructor() {
    this.form = this.fb.group({
      property: ['', Validators.required],
      requestType: ['early_checkin', Validators.required],
      reservation: ['', Validators.required],
    });
  }

  async ngOnInit() {
    const loading = await this.util.showLoading();
    try {
      this.isRightSideContent =
        (await this.storage.retrieve('dialog-right-side-open')) || false;
      this.dialogParams = await this.storage.retrieve('dialog-params');

      await this.checkEditMode();
      await this.cleanupBasedOnMode();

      this.rightSidePanelService.setDialogPageTitle(new TranslationPipe().transform(this.isEditMode ? 'Edit Upsell' : 'Add Upsell'));
      await this.loadData();
    } catch (err) {
      this.errorMessage = err.error ? err.error.message : err.message;
    } finally {
      loading.dismiss();
    }
  }

  /**
   * Clean up data based on the determined mode (edit vs add)
   */
  async cleanupBasedOnMode() {
    if (this.isEditMode) {
      if (this.isRightSideContent && this.editingUpsellData && this.editingUpsellId) {
        await this.storage.save('editingUpsellData', this.editingUpsellData);
        await this.storage.save('editingUpsellId', this.editingUpsellId);
      }
    } else {
      await this.storage.save('editingUpsellData', null);
      await this.storage.save('editingUpsellId', null);
      this.resetFormForAddMode();
    }
  }

  async checkEditMode() {
    const upsellData = this.navCtrl.getParam('editingUpsellData');
    const upsellId = this.navCtrl.getParam('editingUpsellId');

    if (this.dialogParams) {
      const dialogUpsellData = this.dialogParams.editingUpsellData;
      const dialogUpsellId = this.dialogParams.editingUpsellId;

      if (dialogUpsellData && dialogUpsellId) {
        this.isEditMode = true;
        this.editingUpsellId = dialogUpsellId;
        this.editingUpsellData = dialogUpsellData;
        return;
      }
    }

    if (upsellData && upsellId) {
      this.isEditMode = true;
      this.editingUpsellId = upsellId;
      this.editingUpsellData = upsellData;
      if (this.isRightSideContent) {
        await this.storage.save('editingUpsellData', upsellData);
        await this.storage.save('editingUpsellId', upsellId);
      }
    } else {
      this.isEditMode = false;
      this.editingUpsellId = null;
      this.editingUpsellData = null;
    }
  }

  /**
   * Reset form to default values when entering add mode
   */
  resetFormForAddMode() {
    if (this.form) {
      this.form.patchValue({
        property: '',
        requestType: 'early_checkin',
        reservation: ''
      });
      this.reservationItems = [];
    }
  }

  ngOnDestroy() {
    if (!this.isRightSideContent) {
      this.storage.save('editingUpsellData', null);
      this.storage.save('editingUpsellId', null);
    }
  }

  async getAddressData() {
    this.addressFilter = await this.buildAddressFilter();

    if (this.isEditMode && this.editingUpsellData) {
      const propertyId = this.editingUpsellData.property;
      this.form.get('property')?.setValue(propertyId);
      await this.onPropertyChange(propertyId);
    } else {
      this.form.get('property')?.setValue(this.addressFilter[0]?.value);
      this.onPropertyChange(this.addressFilter[0]?.value);
    }
  }

  async buildAddressFilter() {
    this.addressResponse = await this.client.getAddresses();
    return this.client.parseAddressList(this.addressResponse, false);
  }

  @Loading('', true)
  async loadData() {
    try {
      this.requestTypeItems = [
        {
          viewValue: new TranslationPipe().transform('Early Check-In'),
          value: 'early_checkin',
        },
        {
          viewValue: new TranslationPipe().transform('Late Check-Out'),
          value: 'late_checkout',
        },
        {
          viewValue: new TranslationPipe().transform('Midstay Cleaning'),
          value: 'midstay_cleaning',
        },
      ];
      await this.getAddressData();

      if (this.isEditMode && this.editingUpsellData) {
        await this.populateFormWithEditData();
      }

      this.loaded = true;
    } catch (err) {
      this.errorMessage = err.error ? err.error.message : err.message;
    }
  }

  async populateFormWithEditData() {
    try {
      const editData = this.editingUpsellData;

      if (editData.requestType) {
        this.form.get('requestType')?.setValue(editData.requestType);
      }

      if (editData.reservation) {
        setTimeout(async () => {
          this.form.get('reservation')?.setValue(editData.reservation);
        }, 500);
      }

    } catch (_err) {
      // Silent error handling
    }
  }

  async getParam(paramName): Promise<any> {
    let param = this.dialogParams?.[paramName];
    if (param === null || param === undefined) {
      param = this.navCtrl.getParam(paramName);
    }
    if (param === null || param === undefined) {
      param = await this.storage.retrieve(paramName);
    }
    this.storage.save(paramName, param);
    return param;
  }

  async addUpsell() {
    try {
      const formValue = this.form.value;
      const requestType = formValue.requestType;
      const selectedRequestType = this.requestTypeItems.find(item => item.value === requestType);
      const selectedAddress = this.addressFilter.find(item => item.value === formValue.property);
      const selectedReservation = this.reservationItems.find(item => item.value === formValue.reservation);
      const subject = `${selectedRequestType.viewValue} for ${selectedReservation.viewValue} at ${selectedAddress.viewValue}`;
      const data = {
        type: `upsell.${requestType}`,
        data: {
          subject,
          body: `
            property: ${formValue.property},
            reservation: ${formValue.reservation},
            requestType: ${formValue.requestType}
          `,
          emails: [],
          attachments: [],
        },
      };

      if (this.isEditMode && this.editingUpsellId) {
        try {
          const updatePayload = {
            action_name: 'update_request_data',
            params: {
              ...data.data,
              type: data.type,
            },
          }
          await lastValueFrom(this.conciergeApi.updateConciergeItem(parseInt(this.editingUpsellId), updatePayload));
          this.util.showSuccess(new TranslationPipe().transform('Upsell updated successfully'));
        } catch (updateError) {
          const errorMessage = updateError.error ? updateError.error.message : updateError.message;
          this.util.showError(`${new TranslationPipe().transform('Failed to update upsell:')} ${errorMessage}`);
          return;
        }
      } else {
        try {
          await lastValueFrom(this.conciergeApi.createConciergeItem(data));
          this.util.showSuccess(new TranslationPipe().transform('Upsell added successfully'));
        } catch (addError) {
          const errorMessage = addError.error ? addError.error.message : addError.message;
          this.util.showError(`${new TranslationPipe().transform('Failed to add upsell:')} ${errorMessage}`);
          return;
        }
      }

      if (this.isRightSideContent) {
        this.rightSidePanelService.closeRightSidePanel();
      } else {
        this.navCtrl.navigateRoot('upsells');
      }
    } catch (err) {
      this.util.showError(err.error ? err.error.message : err.message);
      this.errorMessage = err.error ? err.error.message : err.message;
    }
  }

  async onPropertyChange(event: any) {
    this.form.get('reservation')?.setValue(null);
    const reservations = await this.guestReservations.getReservations({
      address_id: event,
      per_page: 25,
      state: 'active',
    });
    if (reservations?.body?.length === 0) {
      this.util.showWarning(new TranslationPipe().transform('No reservations found for this property.'));
      return;
    }
    // Sort reservations by year (descending) then by date within year (descending)
    const sortedReservations = reservations.body.sort((a, b) => {
      const dateA = new Date(a.check_in_date);
      const dateB = new Date(b.check_in_date);

      // First sort by year (descending - newest year first)
      const yearDiff = dateB.getFullYear() - dateA.getFullYear();
      if (yearDiff !== 0) {
        return yearDiff;
      }

      // If same year, sort by date (descending - latest date first)
      return dateB.getTime() - dateA.getTime();
    });
    this.reservationItems = sortedReservations.map((reservation) => ({
      viewValue: this.getReservationViewValue(reservation),
      value: reservation.id,
    }));

    if (!this.isEditMode) {
      this.form.get('reservation')?.setValue(this.reservationItems[0]?.value);
    }
  }

  getReservationViewValue(reservation: any) {
    return `${new ParseDateTimePipe().transform(
      reservation.check_in_date,
      'MMM d'
    )} ${new ParseDateTimePipe().transform(
      reservation.check_in_date,
      'h:mma'
    )} - ${new ParseDateTimePipe().transform(
      reservation.check_out_date,
      'MMM d'
    )} ${new ParseDateTimePipe().transform(
      reservation.check_out_date,
      'h:mma '
    )}`;
  }

  async markAsDone() {
    try {
      const updatePayload = {
        action_name: 'mark_upsell_as_done',
        params: {},
      }
      await lastValueFrom(this.conciergeApi.updateConciergeItem(parseInt(this.editingUpsellId), updatePayload));
      this.util.showSuccess(new TranslationPipe().transform('Upsell marked as done'));
      if (this.isRightSideContent) {
        this.rightSidePanelService.closeRightSidePanel();
      } else {
        this.navCtrl.navigateRoot('upsells');
      }
    } catch (err) {
      this.util.showError(err.error ? err.error.message : err.message);
    }
  }
}
